import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Paper,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PersonAdd as PersonAddIcon,
  MoreVert as MoreVertIcon,
  Public as PublicIcon,
  Lock as LockIcon,
  People as PeopleIcon,
  Folder as FolderIcon,
  Business as BusinessIcon,
  ExitToApp as ExitToAppIcon,
  AdminPanelSettings as AdminIcon,
  Person as PersonIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { Group, GroupMember, CreateGroupDTO } from '../models/Group';
import { Collection } from '../models/Collection';
import { GroupInvite, CreateInviteDTO } from '../models/Invite';
import {
  useGroup, useGroupMembers, useGroupCollections, usePendingGroupInvites,
  useUpdateGroup, useDeleteGroup, useLeaveGroup, useAddMember,
  useUpdateMemberRole, useRemoveMember, useCreateInvite, useCancelInvite,
} from '../hooks/useQueries';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const GroupDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'MEMBER' | 'ADMIN'>('MEMBER');
  const [inviteMessage, setInviteMessage] = useState('');
  
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editGroup, setEditGroup] = useState<CreateGroupDTO>({ name: '', description: '', isPublic: false });
  
  const [memberMenuAnchor, setMemberMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedMember, setSelectedMember] = useState<GroupMember | null>(null);

  const groupId = parseInt(id || '0');

  const { data: group, isLoading: groupLoading } = useGroup(groupId);
  const { data: members = [], isLoading: membersLoading } = useGroupMembers(groupId);
  const { data: collections = [] } = useGroupCollections(groupId);
  const isAdmin = group?.currentUserRole === 'ADMIN';
  const { data: pendingInvites = [] } = usePendingGroupInvites(groupId);

  const loading = groupLoading || membersLoading;

  React.useEffect(() => {
    if (group) {
      setEditGroup({
        name: group.name,
        description: group.description || '',
        isPublic: group.isPublic,
        avatarUrl: group.avatarUrl,
      });
    }
  }, [group]);

  const updateMutation = useUpdateGroup();
  const deleteMutation = useDeleteGroup();
  const leaveMutation = useLeaveGroup();
  const addMemberMutation = useAddMember();
  const updateRoleMutation = useUpdateMemberRole();
  const removeMemberMutation = useRemoveMember();
  const createInviteMutation = useCreateInvite();
  const cancelInviteMutation = useCancelInvite();
  const [inviting, setInviting] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    
    try {
      setInviting(true);
      await createInviteMutation.mutateAsync({
        groupId,
        inviteeEmail: inviteEmail,
        role: inviteRole,
        message: inviteMessage || undefined,
      });
      
      setInviteDialogOpen(false);
      setInviteEmail('');
      setInviteMessage('');
      setInviteRole('MEMBER');
    } catch (err: any) {
      setError(err.message || 'Failed to send invite');
    } finally {
      setInviting(false);
    }
  };

  const handleAddMemberDirectly = async () => {
    if (!inviteEmail.trim()) return;
    
    try {
      setInviting(true);
      await addMemberMutation.mutateAsync({ groupId, data: { email: inviteEmail, role: inviteRole } });
      
      setInviteDialogOpen(false);
      setInviteEmail('');
      setInviteRole('MEMBER');
    } catch (err: any) {
      // If direct add fails, try sending an invite instead
      await handleInvite();
    } finally {
      setInviting(false);
    }
  };

  const handleUpdateGroup = async () => {
    try {
      setSaving(true);
      await updateMutation.mutateAsync({ id: groupId, data: editGroup });
      setEditDialogOpen(false);
    } catch (err: any) {
      setError(err.message || 'Failed to update group');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteGroup = async () => {
    if (!window.confirm('Are you sure you want to delete this group?')) return;
    
    try {
      await deleteMutation.mutateAsync(groupId);
      navigate('/groups');
    } catch (err: any) {
      setError(err.message || 'Failed to delete group');
    }
  };

  const handleLeaveGroup = async () => {
    if (!window.confirm('Are you sure you want to leave this group?')) return;
    
    try {
      await leaveMutation.mutateAsync(groupId);
      navigate('/groups');
    } catch (err: any) {
      setError(err.message || 'Failed to leave group');
    }
  };

  const handleMemberAction = async (action: 'promote' | 'demote' | 'remove') => {
    if (!selectedMember) return;
    
    try {
      if (action === 'remove') {
        await removeMemberMutation.mutateAsync({ groupId, userId: selectedMember.userId });
      } else {
        const newRole = action === 'promote' ? 'ADMIN' : 'MEMBER';
        await updateRoleMutation.mutateAsync({ groupId, userId: selectedMember.userId, role: newRole });
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update member');
    } finally {
      setMemberMenuAnchor(null);
      setSelectedMember(null);
    }
  };

  const handleCancelInvite = async (inviteId: number) => {
    try {
      await cancelInviteMutation.mutateAsync(inviteId);
    } catch (err: any) {
      setError(err.message || 'Failed to cancel invite');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!group) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Group not found</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1200, mx: 'auto' }}>
      <Button 
        startIcon={<ArrowBackIcon />} 
        onClick={() => navigate('/groups')} 
        sx={{ mb: 2, minHeight: 44 }}
      >
        Back to Groups
      </Button>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Header */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 2, sm: 3 }, flexDirection: { xs: 'column', sm: 'row' } }}>
          <Avatar sx={{ width: { xs: 64, sm: 80 }, height: { xs: 64, sm: 80 }, bgcolor: 'primary.main', fontSize: { xs: 24, sm: 32 } }}>
            {group.avatarUrl ? (
              <img src={group.avatarUrl} alt={group.name} style={{ width: '100%' }} />
            ) : (
              group.name.charAt(0).toUpperCase()
            )}
          </Avatar>
          
          <Box sx={{ flexGrow: 1, width: { xs: '100%', sm: 'auto' } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
              <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>{group.name}</Typography>
              <Chip
                icon={group.isPublic ? <PublicIcon /> : <LockIcon />}
                label={group.isPublic ? 'Public' : 'Private'}
                size="small"
              />
              {group.currentUserRole && (
                <Chip
                  icon={group.currentUserRole === 'ADMIN' ? <AdminIcon /> : <PersonIcon />}
                  label={group.currentUserRole}
                  color={group.currentUserRole === 'ADMIN' ? 'primary' : 'default'}
                  size="small"
                />
              )}
            </Box>
            
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              {group.description || 'No description'}
            </Typography>

            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PeopleIcon color="action" />
                <Typography variant="body2">{group.memberCount} members</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <FolderIcon color="action" />
                <Typography variant="body2">{group.collectionCount} collections</Typography>
              </Box>
            </Box>

            {group.organizations && group.organizations.length > 0 && (
              <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {group.organizations.map(org => (
                  <Chip key={org.id} icon={<BusinessIcon />} label={org.name} variant="outlined" />
                ))}
              </Box>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'flex-end', sm: 'flex-start' } }}>
            {isAdmin && (
              <>
                <Tooltip title="Edit Group">
                  <IconButton onClick={() => setEditDialogOpen(true)}>
                    <EditIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Invite Member">
                  <IconButton color="primary" onClick={() => setInviteDialogOpen(true)}>
                    <PersonAddIcon />
                  </IconButton>
                </Tooltip>
              </>
            )}
            <Tooltip title="Leave Group">
              <IconButton onClick={handleLeaveGroup}>
                <ExitToAppIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      {/* Tabs */}
      <Tabs 
        value={tabValue} 
        onChange={(_, v) => setTabValue(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          '& .MuiTab-root': {
            minHeight: { xs: 48, sm: 48 },
            fontSize: { xs: '0.875rem', sm: '0.875rem' },
          }
        }}
      >
        <Tab label={`Members (${members.length})`} />
        <Tab label={`Collections (${collections.length})`} />
        {isAdmin && <Tab label={`Pending Invites (${pendingInvites.length})`} />}
      </Tabs>

      <TabPanel value={tabValue} index={0}>
        <List>
          {members.map(member => (
            <ListItem key={member.id} divider>
              <ListItemAvatar>
                <Avatar>{member.userName?.charAt(0) || member.userEmail.charAt(0)}</Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={member.userName || member.userEmail}
                secondary={
                  <React.Fragment>
                    <EmailIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                    {member.userEmail}
                  </React.Fragment>
                }
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  icon={member.role === 'ADMIN' ? <AdminIcon /> : <PersonIcon />}
                  label={member.role}
                  size="small"
                  color={member.role === 'ADMIN' ? 'primary' : 'default'}
                />
                {isAdmin && (
                  <IconButton
                    onClick={(e) => {
                      setMemberMenuAnchor(e.currentTarget);
                      setSelectedMember(member);
                    }}
                    sx={{ ml: 1 }}
                  >
                    <MoreVertIcon />
                  </IconButton>
                )}
              </Box>
            </ListItem>
          ))}
        </List>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {collections.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <FolderIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No collections shared with this group
            </Typography>
          </Box>
        ) : (
          <List>
            {collections.map(collection => (
              <ListItem
                key={collection.id}
                divider
                sx={{ cursor: 'pointer' }}
                onClick={() => navigate(`/collections/${collection.id}`)}
              >
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: collection.color || 'grey.500' }}>
                    <FolderIcon />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={collection.name}
                  secondary={`${collection.imageCount} images • ${collection.ownerUserName || 'Unknown'}`}
                />
                {collection.labels && collection.labels.length > 0 && (
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {collection.labels.slice(0, 3).map(label => (
                      <Chip key={label} label={label} size="small" />
                    ))}
                  </Box>
                )}
              </ListItem>
            ))}
          </List>
        )}
      </TabPanel>

      {isAdmin && (
        <TabPanel value={tabValue} index={2}>
          {pendingInvites.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <EmailIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No pending invites
              </Typography>
            </Box>
          ) : (
            <List>
              {pendingInvites.map(invite => (
                <ListItem key={invite.id} divider>
                  <ListItemAvatar>
                    <Avatar><EmailIcon /></Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={invite.inviteeEmail}
                    secondary={`Invited by ${invite.invitedByUserName} • Expires ${new Date(invite.expiresAt).toLocaleDateString()}`}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
                    <Chip label={invite.role} size="small" />
                    <Button 
                      size="small" 
                      color="error" 
                      onClick={() => handleCancelInvite(invite.id)}
                      sx={{ minHeight: 36, width: { xs: '100%', sm: 'auto' } }}
                    >
                      Cancel
                    </Button>
                  </Box>
                </ListItem>
              ))}
            </List>
          )}
        </TabPanel>
      )}

      {/* Member Action Menu */}
      <Menu
        anchorEl={memberMenuAnchor}
        open={Boolean(memberMenuAnchor)}
        onClose={() => setMemberMenuAnchor(null)}
      >
        {selectedMember?.role === 'MEMBER' && (
          <MenuItem onClick={() => handleMemberAction('promote')}>
            <AdminIcon sx={{ mr: 1 }} /> Promote to Admin
          </MenuItem>
        )}
        {selectedMember?.role === 'ADMIN' && (
          <MenuItem onClick={() => handleMemberAction('demote')}>
            <PersonIcon sx={{ mr: 1 }} /> Demote to Member
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={() => handleMemberAction('remove')} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} /> Remove from Group
        </MenuItem>
      </Menu>

      {/* Invite Dialog */}
      <Dialog 
        open={inviteDialogOpen} 
        onClose={() => setInviteDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            m: { xs: 2, sm: 3 },
            maxHeight: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 64px)' },
          }
        }}
      >
        <DialogTitle>Invite Member</DialogTitle>
        <DialogContent sx={{ pb: { xs: 2, sm: 3 } }}>
          <TextField
            autoFocus
            margin="dense"
            label="Email Address"
            type="email"
            fullWidth
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            sx={{ mb: 2 }}
            inputProps={{ style: { fontSize: 16 } }}
          />
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>Role</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Chip
                icon={<PersonIcon />}
                label="Member"
                onClick={() => setInviteRole('MEMBER')}
                color={inviteRole === 'MEMBER' ? 'primary' : 'default'}
                clickable
              />
              <Chip
                icon={<AdminIcon />}
                label="Admin"
                onClick={() => setInviteRole('ADMIN')}
                color={inviteRole === 'ADMIN' ? 'primary' : 'default'}
                clickable
              />
            </Box>
          </Box>
          <TextField
            margin="dense"
            label="Message (optional)"
            fullWidth
            multiline
            rows={2}
            value={inviteMessage}
            onChange={(e) => setInviteMessage(e.target.value)}
            inputProps={{ style: { fontSize: 16 } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 2 } }}>
          <Button 
            onClick={() => setInviteDialogOpen(false)}
            sx={{ minHeight: 44 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAddMemberDirectly} 
            disabled={!inviteEmail.trim() || inviting}
            sx={{ minHeight: 44 }}
          >
            {inviting ? <CircularProgress size={24} /> : 'Add / Invite'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={() => setEditDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            m: { xs: 2, sm: 3 },
            maxHeight: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 64px)' },
          }
        }}
      >
        <DialogTitle>Edit Group</DialogTitle>
        <DialogContent sx={{ pb: { xs: 2, sm: 3 } }}>
          <TextField
            autoFocus
            margin="dense"
            label="Group Name"
            fullWidth
            value={editGroup.name}
            onChange={(e) => setEditGroup({ ...editGroup, name: e.target.value })}
            sx={{ mb: 2 }}
            inputProps={{ style: { fontSize: 16 } }}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={3}
            value={editGroup.description}
            onChange={(e) => setEditGroup({ ...editGroup, description: e.target.value })}
            sx={{ mb: 2 }}
            inputProps={{ style: { fontSize: 16 } }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Chip
              icon={editGroup.isPublic ? <PublicIcon /> : <LockIcon />}
              label={editGroup.isPublic ? 'Public' : 'Private'}
              onClick={() => setEditGroup({ ...editGroup, isPublic: !editGroup.isPublic })}
              color={editGroup.isPublic ? 'primary' : 'default'}
              clickable
            />
          </Box>
          <Divider sx={{ my: 2 }} />
          <Button 
            color="error" 
            startIcon={<DeleteIcon />} 
            onClick={handleDeleteGroup}
            sx={{ minHeight: 44 }}
          >
            Delete Group
          </Button>
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 2 } }}>
          <Button 
            onClick={() => setEditDialogOpen(false)}
            sx={{ minHeight: 44 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleUpdateGroup} 
            variant="contained" 
            disabled={!editGroup.name.trim() || saving}
            sx={{ minHeight: 44 }}
          >
            {saving ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GroupDetail;
